/**
 * ChatInput - Input area with text field, agent selector, and action buttons
 *
 * Based on mockup: Text input with Add Data, agent dropdown, mic, and send buttons
 */

import { trackEvent } from '@/utils/analytics'
import { useState, useRef, useCallback, useEffect, KeyboardEvent, DragEvent, ChangeEvent } from 'react'
import { Send, Plus, ChevronDown, X, FileText, Image, Square, Volume2, VolumeX, Check, Loader2, Database } from 'lucide-react'
import clsx from 'clsx'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import type { AgentId } from '../../types'
import { AGENTS, AGENT_COLORS, MAX_MESSAGE_LENGTH } from '../../constants'
import ChatVoice from '@/components/ChatInterface/ChatVoice'
import { AgentMentionAutocomplete, filterAgents, type AgentOption } from './AgentMentionAutocomplete'
import { AddDataDropdown } from './AddDataDropdown'
import { apiService } from '@/service/api'

// Supported file types - PDF, DOC, DOCX, and images only
const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.png,.jpg,.jpeg'
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// Placeholder keys (will be translated in component)
const PLACEHOLDER_KEYS = [
  'chatv2:input.placeholder_typewriter_1',
  'chatv2:input.placeholder_typewriter_2',
  'chatv2:input.placeholder_typewriter_3',
]

// Slider animation timing (in ms)
const PLACEHOLDER_DISPLAY_DURATION = 4000 // How long each placeholder is shown
const PLACEHOLDER_TRANSITION_DURATION = 300 // Fade/slide transition time

type FileUploadStatus = 'pending' | 'uploading' | 'processing' | 'ready' | 'error'

interface AttachedFile {
  file: File
  preview?: string
  status: FileUploadStatus
  documentId?: string
  error?: string
}

interface SelectedDocument {
  document_id: string
  name?: string
  doc_type?: string
}

interface ChatInputProps {
  onSend: (content: string, files?: File[]) => void
  onStop?: () => void
  isLoading?: boolean
  activeAgent: AgentId
  onAgentChange: (agentId: AgentId) => void
  availableAgentIds?: AgentId[]
  // Chat toggle (fs-516) — rendered atop the agent dropdown.
  entireDataCatalog?: boolean
  onEntireDataCatalogChange?: (value: boolean) => void
  placeholder?: string
  // Controlled input props
  value?: string
  onChange?: (value: string) => void
  // Speech controls
  audioEnabled?: boolean
  onAudioToggle?: (enabled: boolean) => void
  isSpeaking?: boolean
  onStopSpeaking?: () => void
  isSpeechAvailable?: boolean
  // Add Data dropdown callbacks
  onDataCatalog?: () => void
  // Callback when a file is uploaded and processed - used to link document to board
  onDocumentUploaded?: (documentId: string, docType: string) => void
  // Selected documents from data catalog
  selectedDocuments?: SelectedDocument[]
  onRemoveDocument?: (documentId: string) => void
  onShowAllDocuments?: () => void
}

// Max documents to show as chips before collapsing
const MAX_VISIBLE_DOCS = 2

export function ChatInput({
  onSend,
  onStop,
  isLoading = false,
  activeAgent,
  onAgentChange,
  availableAgentIds,
  entireDataCatalog = false,
  onEntireDataCatalogChange,
  placeholder: _placeholder,
  value: externalValue,
  onChange: onExternalChange,
  audioEnabled = false,
  onAudioToggle,
  isSpeaking = false,
  onStopSpeaking,
  isSpeechAvailable = false,
  onDataCatalog,
  onDocumentUploaded,
  selectedDocuments = [],
  onRemoveDocument,
  onShowAllDocuments,
}: ChatInputProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [showAgentDropdown, setShowAgentDropdown] = useState(false)
  const [showAddDataDropdown, setShowAddDataDropdown] = useState(false)

  // Sync internal state with external value prop (controlled behavior)
  useEffect(() => {
    if (externalValue !== undefined && externalValue !== value) {
      setValue(externalValue)
      // Focus textarea when value is set externally (e.g., from follow-up question)
      if (externalValue && textareaRef.current) {
        textareaRef.current.focus()
        // Move cursor to end of text
        textareaRef.current.setSelectionRange(externalValue.length, externalValue.length)
      }
    }
  }, [externalValue])

  // Keep focus on textarea when re-enabled after sending message
  useEffect(() => {
    if (textareaRef.current && !isLoading) {
      textareaRef.current.focus()
    }
  }, [isLoading])

  // Get translated placeholder texts
  const PLACEHOLDER_TEXTS = [
    t('chatv2:input.placeholder_typewriter_1'),
    t('chatv2:input.placeholder_typewriter_2'),
  ]

  // Slider animation state - cycles through placeholders with smooth transitions
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Slider effect - cycles through placeholder texts with fade/slide animation
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null

    const interval = setInterval(() => {
      // Start exit transition
      setIsTransitioning(true)

      // After transition completes, change text and start enter transition
      timeoutId = setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_KEYS.length)
        setIsTransitioning(false)
        timeoutId = null
      }, PLACEHOLDER_TRANSITION_DURATION)
    }, PLACEHOLDER_DISPLAY_DURATION)

    return () => {
      clearInterval(interval)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, []) // Empty deps - PLACEHOLDER_KEYS.length is a module-level constant
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isRecording, setIsRecording] = useState(false)

  // Agent mention autocomplete state
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false)
  const [mentionFilterQuery, setMentionFilterQuery] = useState('')
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [mentionStartIndex, setMentionStartIndex] = useState(-1)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const inputWrapperRef = useRef<HTMLDivElement>(null)

  // Helper to update both internal and external state
  const updateValue = useCallback((newValue: string) => {
    setValue(newValue)
    onExternalChange?.(newValue)
  }, [onExternalChange])

  const agent = AGENTS[activeAgent]
  const colors = AGENT_COLORS[agent.color]

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim()
    if ((trimmed || attachedFiles.length > 0) && !isLoading) {
      trackEvent('chat-message_sent', {
        message_length: trimmed.length,
        agent_id: activeAgent,
        has_attachments: attachedFiles.length > 0,
        attachment_count: attachedFiles.length,
      })
      const files = attachedFiles.length > 0 ? attachedFiles.map(f => f.file) : undefined
      onSend(trimmed, files)
      updateValue('')
      // Revoke Object URLs before clearing to prevent memory leaks
      attachedFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview)
        }
      })
      setAttachedFiles([])
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }, [value, attachedFiles, isLoading, onSend, updateValue, activeAgent])

  // Upload a single file to the Data Catalog
  const uploadSingleFile = useCallback(async (fileIndex: number, attached: AttachedFile) => {
    // Update status to uploading
    setAttachedFiles(prev => prev.map((f, i) =>
      i === fileIndex ? { ...f, status: 'uploading' as FileUploadStatus } : f
    ))

    try {
      const result = await apiService.uploadFile(attached.file, {
        name: attached.file.name,
        description: 'Uploaded via chat',
        type: 'document',
      }, undefined, { autoRename: true })

      if (!result?.id) {
        throw new Error('Upload failed - no document ID returned')
      }

      // Update status to processing
      setAttachedFiles(prev => prev.map((f, i) =>
        i === fileIndex ? { ...f, status: 'processing' as FileUploadStatus, documentId: result.id } : f
      ))

      // Poll for processing completion
      await waitForProcessing(result.id, fileIndex)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed'
      setAttachedFiles(prev => prev.map((f, i) =>
        i === fileIndex ? { ...f, status: 'error' as FileUploadStatus, error: errorMessage } : f
      ))
      toast.error(t('chatv2:toasts.upload_failed', { filename: attached.file.name, defaultValue: 'Failed to upload {{filename}}' }))
    }
  }, [t])

  // Wait for document processing to complete
  const waitForProcessing = useCallback(async (documentId: string, fileIndex: number): Promise<void> => {
    const maxAttempts = 60 // 2 minutes with 2 second intervals
    let attempts = 0

    const checkStatus = async (): Promise<void> => {
      try {
        const doc = await apiService.getDocument(documentId)

        if (doc.status === 'success' || doc.is_indexed) {
          // Processing complete
          setAttachedFiles(prev => prev.map((f, i) =>
            i === fileIndex ? { ...f, status: 'ready' as FileUploadStatus } : f
          ))
          // Link the uploaded document to the board so AI can access it
          onDocumentUploaded?.(documentId, doc.type || 'document')
          return
        }

        if (doc.status === 'error') {
          throw new Error(doc.error_message || 'Processing failed')
        }

        // Still processing, check again
        attempts++
        if (attempts >= maxAttempts) {
          // Timeout - but mark as ready since upload succeeded
          setAttachedFiles(prev => prev.map((f, i) =>
            i === fileIndex ? { ...f, status: 'ready' as FileUploadStatus } : f
          ))
          // Still link the document even on timeout
          onDocumentUploaded?.(documentId, 'document')
          return
        }

        await new Promise(resolve => setTimeout(resolve, 2000))
        return checkStatus()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Processing failed'
        setAttachedFiles(prev => prev.map((f, i) =>
          i === fileIndex ? { ...f, status: 'error' as FileUploadStatus, error: errorMessage } : f
        ))
      }
    }

    return checkStatus()
  }, [onDocumentUploaded])

  const processFiles = useCallback((files: FileList | null) => {
    if (!files) return

    const newFiles: AttachedFile[] = []
    const startIndex = attachedFiles.length

    Array.from(files).forEach(file => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('chatv2:input.file_too_large', { filename: file.name, maxSize: '10MB', defaultValue: 'File {{filename}} exceeds maximum size of {{maxSize}}' }))
        return
      }

      // Check file type
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(t('chatv2:input.file_type_unsupported', { ext, defaultValue: 'File type .{{ext}} is not supported. Supported: PDF, DOC, DOCX, PNG, JPG, JPEG' }))
        return
      }

      const attachedFile: AttachedFile = {
        file,
        status: 'pending',
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
      }

      newFiles.push(attachedFile)
    })

    if (newFiles.length === 0) return

    // Add files to state with pending status
    setAttachedFiles(prev => [...prev, ...newFiles])

    // Start uploading each file
    newFiles.forEach((attached, i) => {
      uploadSingleFile(startIndex + i, attached)
    })
  }, [attachedFiles.length, uploadSingleFile, t])

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files)
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [processFiles])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    processFiles(e.dataTransfer.files)
  }, [processFiles])

  const removeFile = useCallback((index: number) => {
    setAttachedFiles(prev => {
      const file = prev[index]
      // Revoke object URL if it exists
      if (file.preview) {
        URL.revokeObjectURL(file.preview)
      }
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const handleAddDataClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Handle voice transcript
  const handleVoiceTranscript = useCallback(async (transcript: string) => {
    updateValue(transcript)
  }, [updateValue])

  // Detect @ mentions and update autocomplete state
  const handleValueChange = useCallback((newValue: string) => {
    const trimmedValue = newValue.slice(0, MAX_MESSAGE_LENGTH)
    updateValue(trimmedValue)

    // Find the last @ symbol before cursor position
    const textarea = textareaRef.current
    const cursorPos = textarea?.selectionStart ?? newValue.length

    // Find the @ symbol that starts the current mention
    let atIndex = -1
    for (let i = cursorPos - 1; i >= 0; i--) {
      const char = newValue[i]
      if (char === '@') {
        atIndex = i
        break
      }
      // Stop if we hit whitespace or other special chars before finding @
      if (char === ' ' || char === '\n') break
    }

    if (atIndex !== -1) {
      // Extract the query after @
      const query = newValue.slice(atIndex + 1, cursorPos)

      // Only show autocomplete if we're at the start or after whitespace
      const charBefore = newValue[atIndex - 1]
      const isAtStart = atIndex === 0 || charBefore === ' ' || charBefore === '\n'

      if (isAtStart) {
        setShowMentionAutocomplete(true)
        setMentionFilterQuery(query)
        setMentionStartIndex(atIndex)
        setMentionSelectedIndex(0) // Reset selection when query changes
        return
      }
    }

    // Hide autocomplete if no valid @ mention found
    setShowMentionAutocomplete(false)
    setMentionFilterQuery('')
    setMentionStartIndex(-1)
  }, [])

  // Handle agent selection from autocomplete
  const handleAgentSelect = useCallback((agent: AgentOption) => {
    if (mentionStartIndex === -1) return

    // Always use the full mention format (@ai-mechanical-engineer)
    const mentionToInsert = agent.mentionName

    // Replace @query with the mention
    const before = value.slice(0, mentionStartIndex)
    const after = value.slice(mentionStartIndex + 1 + mentionFilterQuery.length)
    const newValue = `${before}${mentionToInsert} ${after}`

    updateValue(newValue)
    setShowMentionAutocomplete(false)
    setMentionFilterQuery('')
    setMentionStartIndex(-1)

    // Also update the active agent
    onAgentChange(agent.id)

    // Focus textarea and move cursor after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStartIndex + mentionToInsert.length + 1
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
      }
    }, 0)
  }, [value, mentionStartIndex, mentionFilterQuery, onAgentChange, updateValue])

  // Close autocomplete
  const closeMentionAutocomplete = useCallback(() => {
    setShowMentionAutocomplete(false)
    setMentionFilterQuery('')
    setMentionStartIndex(-1)
    setMentionSelectedIndex(0)
  }, [])

  // Async version of handleSubmit for ChatVoice compatibility
  const handleSendMessage = useCallback(async () => {
    handleSubmit()
  }, [handleSubmit])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Handle autocomplete navigation when visible
      if (showMentionAutocomplete) {
        const filteredAgents = filterAgents(mentionFilterQuery)

        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setMentionSelectedIndex((prev) =>
            prev < filteredAgents.length - 1 ? prev + 1 : 0
          )
          return
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setMentionSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredAgents.length - 1
          )
          return
        }

        if (e.key === 'Enter') {
          e.preventDefault()
          const selectedAgent = filteredAgents[mentionSelectedIndex]
          if (selectedAgent) {
            handleAgentSelect(selectedAgent)
          }
          return
        }

        if (e.key === 'Escape') {
          e.preventDefault()
          closeMentionAutocomplete()
          return
        }

        if (e.key === 'Tab') {
          e.preventDefault()
          const selectedAgent = filteredAgents[mentionSelectedIndex]
          if (selectedAgent) {
            handleAgentSelect(selectedAgent)
          }
          return
        }
      }

      // Normal Enter to submit (when autocomplete is not showing)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit, showMentionAutocomplete, mentionFilterQuery, mentionSelectedIndex, handleAgentSelect, closeMentionAutocomplete]
  )

  const handleInput = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [])

  // Check if any files are still uploading/processing
  const hasFilesProcessing = attachedFiles.some(
    f => f.status === 'pending' || f.status === 'uploading' || f.status === 'processing'
  )

  // Only allow send when there's content, not loading, and no files are processing
  const canSend = (value.trim().length > 0 || attachedFiles.some(f => f.status === 'ready')) && !isLoading && !hasFilesProcessing

  // Helper to get file icon
  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="h-4 w-4" />
    return <FileText className="h-4 w-4" />
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div ref={inputWrapperRef} className="relative">
      {/* Agent mention autocomplete — uses portal to escape overflow-hidden ancestors */}
      <AgentMentionAutocomplete
        isVisible={showMentionAutocomplete}
        selectedIndex={mentionSelectedIndex}
        filterQuery={mentionFilterQuery}
        onSelect={handleAgentSelect}
        onClose={closeMentionAutocomplete}
        availableAgentIds={availableAgentIds}
        anchorRef={inputWrapperRef}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Main input container */}
      <div
        className={clsx(
          'relative border rounded-2xl bg-white dark:bg-dark-card shadow-sm',
          isDragOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Attached files and documents preview */}
        {(attachedFiles.length > 0 || selectedDocuments.length > 0) && (
          <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2">
            {/* Uploaded files with status indicators */}
            {attachedFiles.map((attached, index) => (
              <div
                key={`file-${index}`}
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg group relative',
                  attached.status === 'error' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-gray-100 dark:bg-dark-accent'
                )}
              >
                {/* Loading overlay for uploading/processing files */}
                {(attached.status === 'uploading' || attached.status === 'processing' || attached.status === 'pending') && (
                  <div className="absolute inset-0 bg-white/60 dark:bg-dark-card/60 rounded-lg flex items-center justify-center">
                    <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                  </div>
                )}

                {/* Status badge */}
                {attached.status === 'ready' && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                    <Check className="h-2.5 w-2.5 text-white" />
                  </div>
                )}
                {attached.status === 'error' && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
                    <X className="h-2.5 w-2.5 text-white" />
                  </div>
                )}

                {attached.preview ? (
                  <img
                    src={attached.preview}
                    alt={attached.file.name}
                    className="h-6 w-6 object-cover rounded"
                  />
                ) : (
                  getFileIcon(attached.file)
                )}
                <span className={clsx(
                  'text-sm max-w-[150px] truncate',
                  attached.status === 'error' ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                )}>
                  {attached.file.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                  {formatFileSize(attached.file.size)}
                </span>
                {/* Remove button - only show when not uploading */}
                {attached.status !== 'uploading' && attached.status !== 'pending' && (
                  <button
                    onClick={() => removeFile(index)}
                    className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3 text-gray-500 dark:text-dark-text-muted" />
                  </button>
                )}
              </div>
            ))}
            {/* Selected documents from data catalog - show max 2 + overflow */}
            {selectedDocuments.slice(0, MAX_VISIBLE_DOCS).map((doc) => (
              <div
                key={`doc-${doc.document_id}`}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg group"
              >
                <FileText className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[150px] truncate">
                  {doc.name || t('chatv2:messages.document', { defaultValue: 'Document' })}
                </span>
                {onRemoveDocument && (
                  <button
                    onClick={() => onRemoveDocument(doc.document_id)}
                    className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3 text-blue-500" />
                  </button>
                )}
              </div>
            ))}
            {/* Show "+N more" button if there are more documents */}
            {selectedDocuments.length > MAX_VISIBLE_DOCS && (
              <button
                onClick={onShowAllDocuments}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                {t('chatv2:documents.show_more', { count: selectedDocuments.length - MAX_VISIBLE_DOCS, defaultValue: '+{{count}} more' })}
              </button>
            )}
          </div>
        )}

        {/* Textarea */}
        <div className="relative">
          {/* Animated placeholder overlay - only show when no value */}
          {!value && !isDragOver && (
            <div
              className={clsx(
                'absolute left-4 top-3 pointer-events-none text-gray-400 dark:text-gray-500',
                'transition-all ease-in-out',
                isTransitioning ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'
              )}
              style={{ transitionDuration: `${PLACEHOLDER_TRANSITION_DURATION}ms` }}
            >
              {PLACEHOLDER_TEXTS[placeholderIndex]}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            placeholder={isDragOver ? t('chatv2:input.drop_files_here') : ''}
            disabled={isLoading}
            rows={1}
            autoFocus
            className={clsx(
              'w-full px-4 py-3 pr-24 resize-none',
              'bg-transparent border-none outline-none',
              'text-gray-900 dark:text-dark-text placeholder:text-gray-400 dark:placeholder:text-gray-500',
              'disabled:opacity-50'
            )}
          />

          {/* Character count - show when approaching limit */}
          {value.length > MAX_MESSAGE_LENGTH * 0.8 && (
            <div
              className={clsx(
                'absolute right-3 top-3 text-xs',
                value.length >= MAX_MESSAGE_LENGTH ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'
              )}
            >
              {value.length}/{MAX_MESSAGE_LENGTH}
            </div>
          )}
        </div>

        {/* Bottom toolbar */}
        <div className="flex items-center justify-between px-3 pb-2">
          {/* Left side: Add Data + Agent selector */}
          <div className="flex items-center gap-2">
            {/* Add Data dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowAddDataDropdown(!showAddDataDropdown)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
                  'text-sm text-gray-600 dark:text-gray-400',
                  'hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors'
                )}
              >
                <Plus className="h-4 w-4" />
                <span>{t('chatv2:input.add_data')}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>

              {/* Add Data dropdown menu */}
              {showAddDataDropdown && (
                <AddDataDropdown
                  onUploadFiles={handleAddDataClick}
                  onDataCatalog={() => onDataCatalog?.()}
                  onClose={() => setShowAddDataDropdown(false)}
                />
              )}
            </div>

            {/* Agent selector */}
            <div className="relative">
              <button
                onClick={() => setShowAgentDropdown(!showAgentDropdown)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
                  'text-sm font-medium',
                  colors.bg,
                  colors.text,
                  'hover:opacity-80 transition-opacity'
                )}
              >
                {agent.label}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>

              {/* Agent dropdown */}
              {showAgentDropdown && (
                <div className="absolute bottom-full left-0 mb-2 w-60 py-1 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-20">
                  {/* Chat toggle (fs-516) — independent boolean flag;
                      clicking it does NOT close the dropdown or change the
                      selected agent. */}
                  <button
                    type="button"
                    aria-pressed={entireDataCatalog}
                    onClick={() => onEntireDataCatalogChange?.(!entireDataCatalog)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-2 text-left',
                      'hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors'
                    )}
                  >
                    {/* Presentational checkbox — decorative (aria-hidden); the
                        button conveys the toggle state via aria-pressed. Mirrors
                        the visual of @/components/ui/checkbox without nesting an
                        interactive control inside the button. */}
                    <span
                      aria-hidden="true"
                      data-testid="entire-catalog-checkbox"
                      data-state={entireDataCatalog ? 'checked' : 'unchecked'}
                      className={clsx(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border bg-background',
                        entireDataCatalog
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input'
                      )}
                    >
                      {entireDataCatalog && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <Database className="h-4 w-4 flex-shrink-0 text-gray-600 dark:text-gray-400" />
                    <span className="flex-1 text-sm font-medium text-gray-900 dark:text-dark-text">
                      {t('chatv2:input.entire_data_catalog')}
                    </span>
                  </button>
                  <div className="my-1 border-t border-gray-200 dark:border-dark-border" />
                  {Object.values(AGENTS).filter((a) => !availableAgentIds || availableAgentIds.includes(a.id)).map((a) => {
                    const agentColors = AGENT_COLORS[a.color]
                    return (
                      <button
                        key={a.id}
                        onClick={() => {
                          if (a.id !== activeAgent) {
                            trackEvent('chat-agent_switched', {
                              from_agent: activeAgent,
                              to_agent: a.id,
                            })
                          }
                          onAgentChange(a.id)
                          setShowAgentDropdown(false)
                        }}
                        className={clsx(
                          'w-full flex items-center gap-2 px-3 py-2 text-left',
                          'hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors',
                          activeAgent === a.id && 'bg-gray-50 dark:bg-dark-muted'
                        )}
                      >
                        <div className="relative w-6 h-6 rounded-full flex-shrink-0">
                          <img
                            src={a.avatarUrl}
                            alt={a.label}
                            className="w-6 h-6 rounded-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const fallback = target.nextElementSibling as HTMLElement
                              if (fallback) fallback.classList.remove('hidden')
                            }}
                          />
                          {/* Fallback letter avatar */}
                          <div
                            className={clsx(
                              'hidden w-6 h-6 rounded-full flex items-center justify-center',
                              'text-xs font-bold',
                              agentColors.bg,
                              agentColors.text
                            )}
                          >
                            {a.label.charAt(0)}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-dark-text">
                            {a.label}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right side: Audio toggle + Voice + Stop/Send */}
          <div className="flex items-center gap-1">
            {/* Audio output toggle (TTS) */}
            {isSpeechAvailable && onAudioToggle && (
              <button
                onClick={() => onAudioToggle(!audioEnabled)}
                className={clsx(
                  'p-2 rounded-full transition-colors',
                  audioEnabled
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/40'
                    : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-accent hover:text-gray-600 dark:hover:text-gray-400'
                )}
                title={audioEnabled ? t('chatv2:input.disable_audio') : t('chatv2:input.enable_audio')}
              >
                {audioEnabled ? (
                  <Volume2 className="h-5 w-5" />
                ) : (
                  <VolumeX className="h-5 w-5" />
                )}
              </button>
            )}

            {/* Stop speaking button (when AI is speaking) */}
            {isSpeaking && onStopSpeaking && (
              <button
                onClick={onStopSpeaking}
                className="p-2 rounded-full bg-orange-500 text-white hover:bg-orange-600 transition-colors animate-pulse"
                title={t('chatv2:input.stop_speaking')}
              >
                <Square className="h-5 w-5" />
              </button>
            )}

            {/* Voice input */}
            <ChatVoice
              onTranscript={handleVoiceTranscript}
              handleSendMessage={handleSendMessage}
              isRecording={isRecording}
              setIsRecording={setIsRecording}
              useAzure={true}
              borderless
            />

            {/* Stop button (when loading) or Send button */}
            {isLoading && onStop ? (
              <button
                onClick={onStop}
                className={clsx(
                  'p-2 rounded-full transition-colors',
                  'bg-red-500 text-white hover:bg-red-600'
                )}
                title={t('chatv2:input.stop', { defaultValue: 'Stop generating' })}
              >
                <Square className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSend}
                className={clsx(
                  'p-2 rounded-full transition-colors',
                  canSend
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200'
                    : 'bg-gray-100 dark:bg-dark-accent text-gray-400 dark:text-gray-500 cursor-not-allowed'
                )}
              >
                <Send className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {showAgentDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowAgentDropdown(false)}
        />
      )}
    </div>
  )
}

export default ChatInput
